/**
 * Shared presentational + formatting primitives for the Customer detail cards.
 * Extracted so CustomerDetailSidebar and ActivityTimelineCard share one source
 * of truth (no copy-pasted utilities) and each file stays under the size limit.
 */

import { cn } from "@/lib/utils";

export function CardTitle({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 mb-4">
      <span className="text-gray-400 dark:text-gray-500">{icon}</span>
      <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
        {children}
      </span>
    </div>
  );
}

export function EmptyValue() {
  return <span className="text-gray-400 dark:text-gray-500 italic">—</span>;
}

/** A compact labelled metric tile (used across the financial + summary cards). */
export function StatTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: "amber" | "default";
}) {
  return (
    <div className="rounded-sm bg-muted/40 dark:bg-white/[0.03] px-4 py-3.5">
      <div className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
        {label}
      </div>
      <div
        className={cn(
          "text-[16px] font-bold leading-tight mt-1.5 break-words",
          accent === "amber" ? "text-amber-600 dark:text-amber-400" : "text-gray-900 dark:text-gray-100",
        )}
      >
        {value}
      </div>
    </div>
  );
}

export function formatMoney(amount: number | undefined | null, currency: string): string {
  const value = typeof amount === "number" ? amount : 0;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "THB",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    // Unknown currency code → fall back to plain number + code
    return `${value.toLocaleString("en-US")} ${currency || ""}`.trim();
  }
}

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "7 Aug 2026" — accepts ERPNext ("YYYY-MM-DD [HH:MM:SS]") and ISO ("...T..."). */
export function formatReadableDate(value?: string): string {
  if (!value) return "—";
  const [y, m, d] = value.split(/[ T]/)[0].split("-");
  if (!y || !m || !d) return value;
  const monthName = MONTHS[Number(m) - 1] ?? m;
  return `${Number(d)} ${monthName} ${y}`;
}

/** Compact "7 Aug" (no year) — used in tight sidebar rows. */
export function formatShortDate(value?: string | null): string {
  if (!value) return "";
  const [y, m, d] = value.split(/[ T]/)[0].split("-");
  if (!y || !m || !d) return value;
  return `${Number(d)} ${MONTHS[Number(m) - 1] ?? m}`;
}

/** Parse ERPNext / ISO timestamps into a Date (space→T so Safari accepts it). */
export function parseTs(ts?: string | null): Date | null {
  if (!ts) return null;
  const d = new Date(ts.includes("T") ? ts : ts.replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d;
}

/** Short relative time: "now", "5m", "3h", "2d", "4w", else "7 Aug". */
export function timeAgo(ts?: string | null): string {
  const then = parseTs(ts);
  if (!then) return "";
  const diff = Date.now() - then.getTime();
  if (diff < 0) return "now";
  const min = Math.floor(diff / 60000);
  if (min < 1) return "now";
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w`;
  return formatShortDate(ts);
}
